"use client";

import { cn } from "@/lib/utils";
import makeSlug from "@/utils/makeSlug";
import Link from "@app/_components/Link";
import { useMarket } from "@features/market/useMarket";
import useTranslate from "@app/_hooks/useTranslate";
import { Button } from "@ui/button";
import { Field, FieldError, FieldLabel, FieldSet } from "@ui/field";
import { Heading } from "@ui/heading";
import { Paragraph } from "@ui/paragraph";
import { FirebaseError } from "firebase/app";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState, type ComponentProps, type ElementType } from "react";
import useSignIn from "../hooks/useSignIn";
import useSigninForm from "../hooks/useSigninForm";
import getFieldErrorState from "../utils/getFieldErrorState";
import isIgnorableProviderError from "../utils/isIgnorableProviderError";

export default function SignInForm({
  className,
  callback,
  redirectOnSuccess = true,
  defaultEmail,
  children,
  header: HeaderComponent = Heading,
  description: DescriptionComponent = Paragraph,
  ...props
}: ComponentProps<"div"> & {
  callback?: () => void;
  redirectOnSuccess?: boolean;
  defaultEmail?: string;
  header?: ElementType;
  description?: ElementType;
}) {
  const t = useTranslate();
  const router = useRouter();
  const market = useMarket();
  const signIn = useSignIn();

  const completeSignIn = () => {
    if (redirectOnSuccess) {
      router.push(makeSlug(market.siteLocale, "/account/orders"));
    }
    callback?.();
  };

  const form = useSigninForm({
    onSuccess: completeSignIn,
    defaultEmail,
  });

  const [awaitingProviderSignIn, setAwaitingProviderSignIn] = useState(false);

  const onProviderSignIn = async (strategy: "google" | "facebook") => {
    setAwaitingProviderSignIn(true);
    try {
      await signIn({ strategy });
      completeSignIn();
    } catch (error) {
      const code =
        error instanceof FirebaseError
          ? error.code
          : error instanceof Error
            ? error.message
            : undefined;

      if (isIgnorableProviderError(code)) {
        return;
      }

      form.setErrorMap({ onSubmit: "internalError" });
    } finally {
      setAwaitingProviderSignIn(false);
    }
  };

  return (
    <div className={cn("grid grid-flow-row gap-4", className)} {...props}>
      <HeaderComponent className="text-2xl">{t("login")}</HeaderComponent>
      <DescriptionComponent>{t("loginDialogSubheading")}</DescriptionComponent>
      <div className="grid grid-cols-2 gap-2 border-b pb-4">
        <Button
          disabled={awaitingProviderSignIn}
          onClick={() => onProviderSignIn("google")}
          variant="outline"
        >
          {awaitingProviderSignIn ? (
            <Loader2 className="size-5 animate-spin" strokeWidth={2} />
          ) : (
            "Google"
          )}
        </Button>
        <Button
          disabled={awaitingProviderSignIn}
          onClick={() => onProviderSignIn("facebook")}
          variant="outline"
        >
          {awaitingProviderSignIn ? (
            <Loader2 className="size-5 animate-spin" strokeWidth={2} />
          ) : (
            "Facebook"
          )}
        </Button>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          form.handleSubmit();
        }}
        className="border-b"
      >
        <FieldSet>
          <form.AppField
            name="email"
            children={(field) => (
              <Field
                invalid={!field.getMeta().isValid}
                dirty={field.getMeta().isDirty}
                touched={field.getMeta().isTouched}
              >
                <FieldLabel placeholder>{t("email")}</FieldLabel>
                <field.Input
                  autoComplete="email"
                  placeholder={t("email")}
                  type="email"
                  value={field.state.value}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={() => field.handleBlur()}
                  disabled={awaitingProviderSignIn}
                />
                {getFieldErrorState(field) && (
                  <FieldError>{t(field.getMeta().errors[0]!)}</FieldError>
                )}
              </Field>
            )}
          />
          <form.AppField
            name="password"
            children={(field) => (
              <Field
                invalid={!field.getMeta().isValid}
                dirty={field.getMeta().isDirty}
                touched={field.getMeta().isTouched}
              >
                <FieldLabel placeholder>{t("passwordLabel")}</FieldLabel>
                <field.Input
                  autoComplete="current-password"
                  placeholder={t("passwordLabel")}
                  type="password"
                  value={field.state.value}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={() => field.handleBlur()}
                  disabled={awaitingProviderSignIn}
                />
                {getFieldErrorState(field) && (
                  <FieldError>{t(field.getMeta().errors[0]!)}</FieldError>
                )}
              </Field>
            )}
          />
          <form.Subscribe
            selector={(state) => [
              state.isValid,
              state.isDirty,
              state.isSubmitting,
            ]}
            children={([isValid, isDirty, isSubmitting]) => (
              <Button
                className="w-full"
                type="submit"
                disabled={
                  !isDirty || !isValid || isSubmitting || awaitingProviderSignIn
                }
              >
                {isSubmitting ? (
                  <Loader2 className="size-5 animate-spin" strokeWidth={2} />
                ) : (
                  t("login")
                )}
              </Button>
            )}
          />
        </FieldSet>
        <form.Subscribe
          selector={(state) => state.errors}
          children={(errors) =>
            errors.map((error) => (
              <form.FormError
                key={error as string}
                type="error"
                className="mt-4"
              >
                {t(error as string)}
              </form.FormError>
            ))
          }
        />
      </form>
      {children}
      <Paragraph className="text-center">
        {t("noAccount")}
        <Link
          locale={market.siteLocale}
          href="/register"
          className="all-small-caps block tracking-wider uppercase underline underline-offset-4"
        >
          {t("register")}
        </Link>
      </Paragraph>
    </div>
  );
}
